import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Layers, Package, CheckCircle2, XCircle, Palette, Server, LayoutGrid, Settings2, Search, Filter, ArrowLeft, RefreshCcw, Copy, Paintbrush, Dna, Eye, Rocket, Archive, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { notifyError } from "@/lib/api";
import PlatformLayout from "@/components/PlatformLayout";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import TemplateCloneWizard from "./templates/TemplateCloneWizard";
import TemplateLivePreview from "./templates/TemplateLivePreview";

const CATEGORIES = ["Retail & Ecommerce", "Fashion", "Beauty", "Food & Beverage",
  "Automotive", "Home & Living", "Electronics", "Sports", "Vape"];

const STATUS_TONE = {
  PUBLISHED: "emerald",
  ACTIVE: "emerald",
  DRAFT: "slate",
  DEPRECATED: "rose",
  ARCHIVED: "darkred",
};

function StatusBadge({ status }) {
  const t = STATUS_TONE[status] || "slate";
  const cls = {
    emerald: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    amber: "bg-amber-500/15 text-amber-300 border-amber-500/30",
    rose: "bg-rose-500/15 text-rose-300 border-rose-500/30",
    darkred: "bg-red-950/60 text-red-300 border-red-800/60",
    slate: "bg-white/10 text-white/70 border-white/20",
  }[t];
  return <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-black uppercase tracking-widest ${cls}`} data-testid="tr-status-badge">{status}</span>;
}

function SummaryCard({ label, value, tone = "slate", testid }) {
  const cls = {
    slate: "border-white/10 text-white",
    emerald: "border-emerald-500/30 text-emerald-300",
    amber: "border-amber-500/30 text-amber-300",
    rose: "border-rose-500/30 text-rose-300",
    cyan: "border-cyan-500/30 text-cyan-300",
  }[tone];
  return (
    <div className={`rounded-2xl border ${cls} bg-white/5 px-4 py-3`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-white/50 font-black">{label}</div>
      <div className="text-3xl font-display font-black mt-1">{value ?? "—"}</div>
    </div>
  );
}

function ThemeSwatch({ color }) {
  return <span className="inline-block w-4 h-4 rounded-full border border-white/20" style={{ backgroundColor: color || "#334155" }} />;
}

function TemplateCard({ tpl, onClick, onClone, onDesign, onPreview, onPublish, onArchive, busy }) {
  const featureCount = tpl.capability_count ?? Object.values(tpl.feature_flags || {}).filter(Boolean).length;
  const isPublished = ["PUBLISHED", "ACTIVE"].includes(tpl.status);
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === "Enter") onClick(); }}
      className="text-left rounded-2xl border border-white/10 bg-white/5 hover:bg-white/10 transition-all duration-300 p-5 group hover:border-cyan-500/40 cursor-pointer"
      data-testid={`tr-card-${tpl.slug}`}
    >
      <div className="flex items-start gap-3 mb-3">
        {tpl.thumbnail ? (
          <img src={tpl.thumbnail} alt="" className="w-12 h-12 rounded-xl object-cover shrink-0 border border-white/10" />
        ) : (
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-purple-500 flex items-center justify-center shrink-0">
            <Package size={24} className="text-white" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-white font-display font-black text-lg truncate">{tpl.name}</h3>
            <span className="text-[10px] font-mono text-white/50">v{tpl.version}</span>
          </div>
          <div className="text-white/50 text-xs font-mono mt-0.5 truncate">{tpl.slug}</div>
        </div>
        <StatusBadge status={tpl.status} />
      </div>
      <p className="text-white/60 text-sm line-clamp-2 mb-3">{tpl.description || "—"}</p>
      <div className="grid grid-cols-2 gap-2 text-xs mb-3">
        <div className="flex items-center gap-1.5 text-white/60">
          <Layers size={12} /> <span>{tpl.category}</span>
        </div>
        <div className="flex items-center gap-1.5 text-white/60">
          <ThemeSwatch color={tpl.design?.primary_color || tpl.branding?.theme_color} />
          <span className="font-mono">{tpl.design?.primary_color || tpl.branding?.theme_color || "—"}</span>
        </div>
        <div className="flex items-center gap-1.5 text-white/60">
          <Dna size={12} className="text-purple-300" /> <span className="truncate">{tpl.dna?.business_category || "—"}</span>
        </div>
        <div className="flex items-center gap-1.5 text-white/60">
          <CheckCircle2 size={12} className="text-emerald-400" /> <span>{featureCount} features</span>
        </div>
      </div>
      <div className="flex items-center gap-1.5 flex-wrap pt-2 border-t border-white/5">
        <button
          onClick={(e) => { e.stopPropagation(); onPreview(); }}
          className="flex items-center gap-1 rounded-lg border border-white/15 bg-white/5 text-white/80 px-2.5 py-1 text-[11px] font-black hover:bg-white/15"
          data-testid={`tr-preview-${tpl.slug}`}
        >
          <Eye size={11} /> Preview
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onDesign(); }}
          className="flex items-center gap-1 rounded-lg border border-cyan-500/30 bg-cyan-500/10 text-cyan-200 px-2.5 py-1 text-[11px] font-black hover:bg-cyan-500/20"
          data-testid={`tr-designer-${tpl.slug}`}
        >
          <Paintbrush size={11} /> Edit
        </button>
        {!isPublished && (
          <button
            onClick={(e) => { e.stopPropagation(); onPublish(); }}
            disabled={busy}
            className="flex items-center gap-1 rounded-lg border border-emerald-500/30 bg-emerald-500/10 text-emerald-200 px-2.5 py-1 text-[11px] font-black hover:bg-emerald-500/20 disabled:opacity-50"
            data-testid={`tr-publish-${tpl.slug}`}
          >
            <Rocket size={11} /> Publish
          </button>
        )}
        {!tpl.is_system && tpl.status !== "ARCHIVED" && (
          <button
            onClick={(e) => { e.stopPropagation(); onArchive(); }}
            disabled={busy}
            className="flex items-center gap-1 rounded-lg border border-red-800/50 bg-red-950/40 text-red-300 px-2.5 py-1 text-[11px] font-black hover:bg-red-950/70 disabled:opacity-50"
            data-testid={`tr-archive-${tpl.slug}`}
          >
            <Archive size={11} /> Archive
          </button>
        )}
        <button
          onClick={(e) => { e.stopPropagation(); onClone(); }}
          className="flex items-center gap-1 rounded-lg border border-purple-500/30 bg-purple-500/10 text-purple-200 px-2.5 py-1 text-[11px] font-black hover:bg-purple-500/20"
          data-testid={`tr-clone-${tpl.slug}`}
        >
          <Copy size={11} /> Clone
        </button>
        {tpl.cloned_from && (
          <span className="ml-auto text-[9px] text-white/30 font-mono truncate">clone of {tpl.cloned_from.slug}</span>
        )}
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, children, testid }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-4">
        <Icon size={16} className="text-cyan-400" />
        <h3 className="text-white font-display font-black text-sm uppercase tracking-widest">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function KeyValueGrid({ items, columns = 2 }) {
  const entries = Object.entries(items || {});
  return (
    <div className={`grid grid-cols-1 sm:grid-cols-${columns} gap-2`}>
      {entries.map(([k, v]) => (
        <div key={k} className="flex justify-between items-center border-b border-white/5 py-1.5 text-xs">
          <span className="text-white/50 font-mono uppercase truncate">{k.replace(/_/g, " ")}</span>
          <span className="text-white font-mono ml-2 truncate">{v === null || v === undefined || v === "" ? "—" : String(v)}</span>
        </div>
      ))}
    </div>
  );
}

function FeatureFlagsGrid({ flags }) {
  const entries = Object.entries(flags || {});
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2" data-testid="tr-detail-features">
      {entries.map(([k, v]) => (
        <div
          key={k}
          className={`flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-xs ${v ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-200" : "border-white/10 bg-white/5 text-white/40"}`}
          data-testid={`tr-flag-${k}`}
        >
          {v ? <CheckCircle2 size={12} className="text-emerald-400" /> : <XCircle size={12} className="text-white/30" />}
          <span className="font-mono uppercase">{k.replace(/_/g, " ")}</span>
        </div>
      ))}
    </div>
  );
}

function DetailView({ tpl, onBack }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [cfg, setCfg] = useState(null);
  const [cfgLoading, setCfgLoading] = useState(false);

  React.useEffect(() => {
    if (tab === "configuration" && !cfg) {
      setCfgLoading(true);
      api.get(`/platform/template-configurations/${tpl.slug}`)
        .then((r) => setCfg(r.data))
        .catch(() => setCfg(null))
        .finally(() => setCfgLoading(false));
    }
  }, [tab, cfg, tpl.slug]);

  const CONFIG_GROUPS = ["branding", "payment", "shipping", "inventory",
    "notification", "email", "warehouse", "invoice", "tax", "localization",
    "pwa", "seo", "analytics", "search", "customer"];

  return (
    <div className="space-y-5" data-testid="tr-detail">
      <div className="flex items-center gap-3">
        <Button variant="ghost" onClick={onBack} className="text-white/70 hover:text-white hover:bg-white/10" data-testid="tr-detail-back">
          <ArrowLeft size={14} className="mr-1" /> Back
        </Button>
        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-cyan-500 to-purple-500 flex items-center justify-center">
          <Package size={26} className="text-white" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-white font-display font-black text-2xl">{tpl.name}</h1>
            <span className="text-white/50 font-mono text-sm">v{tpl.version}</span>
            <StatusBadge status={tpl.status} />
          </div>
          <div className="text-white/60 text-xs font-mono mt-1">{tpl.slug} · {tpl.category}</div>
        </div>
        <Button
          onClick={() => navigate(`/platform/template-registry/${tpl.id}/designer`)}
          className="bg-gradient-to-r from-cyan-500 to-purple-500 text-white font-black"
          data-testid="tr-detail-open-designer"
        >
          <Paintbrush size={13} className="mr-1" /> Open Designer
        </Button>
      </div>

      <div className="flex items-center gap-1 border-b border-white/10" data-testid="tr-detail-tabs">
        {[
          { key: "overview", label: "Overview" },
          { key: "configuration", label: "Configuration" },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-black uppercase tracking-widest border-b-2 transition-all ${tab === t.key ? "border-cyan-400 text-cyan-300" : "border-transparent text-white/50 hover:text-white/80"}`}
            data-testid={`tr-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <>
          <div className="rounded-xl border border-white/10 bg-white/5 p-4">
            <p className="text-white/70 text-sm leading-relaxed">{tpl.description}</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Section title="Template DNA" icon={Dna} testid="tr-detail-dna">
              <KeyValueGrid items={{
                ...tpl.dna,
                inventory_units: Array.isArray(tpl.dna?.inventory_units) ? tpl.dna.inventory_units.join(", ") : tpl.dna?.inventory_units,
                homepage_sections: Array.isArray(tpl.dna?.homepage_sections) ? tpl.dna.homepage_sections.join(" · ") : tpl.dna?.homepage_sections,
              }} />
            </Section>
            <Section title="Design" icon={Paintbrush} testid="tr-detail-design">
              <div className="flex items-center gap-2 mb-3">
                {["primary_color", "accent_color", "secondary_color", "background_color"].map((k) => (
                  <ThemeSwatch key={k} color={tpl.design?.[k]} />
                ))}
                <span className="text-white/60 text-xs font-mono">Palette</span>
              </div>
              <KeyValueGrid items={tpl.design} />
            </Section>
            <Section title="Demo Content" icon={Package} testid="tr-detail-demo">
              <KeyValueGrid items={{
                brand_name: tpl.demo_content?.brand_name,
                categories: (tpl.demo_content?.categories || []).length,
                products: (tpl.demo_content?.products || []).length,
                banners: (tpl.demo_content?.banners || []).length,
              }} />
            </Section>
            <Section title="Branding" icon={Palette} testid="tr-detail-branding">
              <div className="flex items-center gap-3 mb-3">
                <ThemeSwatch color={tpl.branding?.theme_color} />
                <ThemeSwatch color={tpl.branding?.accent_color} />
                <span className="text-white/60 text-xs font-mono">Theme + Accent</span>
              </div>
              <KeyValueGrid items={tpl.branding} />
            </Section>

            <Section title="Provider Defaults" icon={Server} testid="tr-detail-providers">
              <KeyValueGrid items={tpl.provider_defaults} />
              <div className="mt-3 text-[10px] text-amber-300/70 font-mono uppercase tracking-widest">
                ⚠ Nama provider saja — credential ada di tenant configuration
              </div>
            </Section>

            <Section title="Layout Defaults" icon={LayoutGrid} testid="tr-detail-layouts">
              <KeyValueGrid items={tpl.layout_defaults} />
            </Section>

            <Section title="Default Configuration" icon={Settings2} testid="tr-detail-config">
              <KeyValueGrid items={tpl.default_configuration} />
            </Section>
          </div>

          <Section title={`Feature Flags (${tpl.capability_count}/15 active)`} icon={CheckCircle2} testid="tr-detail-features-section">
            <FeatureFlagsGrid flags={tpl.feature_flags} />
          </Section>

          {tpl.business_profile && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Section title="Business Profile" icon={Layers} testid="tr-detail-business-profile">
                <KeyValueGrid items={tpl.business_profile} />
              </Section>
              <Section title="Catalog Profile" icon={LayoutGrid} testid="tr-detail-catalog-profile">
                <KeyValueGrid items={tpl.catalog_profile} />
              </Section>
              <Section title="Checkout Profile" icon={Settings2} testid="tr-detail-checkout-profile">
                <KeyValueGrid items={tpl.checkout_profile} />
              </Section>
              <Section title="Admin Profile" icon={Server} testid="tr-detail-admin-profile">
                <KeyValueGrid items={{
                  ...tpl.admin_profile,
                  default_menu: Array.isArray(tpl.admin_profile?.default_menu)
                    ? tpl.admin_profile.default_menu.join(", ")
                    : tpl.admin_profile?.default_menu,
                }} />
              </Section>
              <Section title="SEO Profile" icon={Palette} testid="tr-detail-seo-profile">
                <KeyValueGrid items={tpl.seo_profile} />
              </Section>
              <Section title="PWA Profile" icon={Package} testid="tr-detail-pwa-profile">
                <KeyValueGrid items={tpl.pwa_profile} />
              </Section>
            </div>
          )}

          <div className="text-[10px] text-white/40 font-mono text-right">
            Created: {tpl.created_at?.slice(0, 19).replace("T", " ")} · Updated: {tpl.updated_at?.slice(0, 19).replace("T", " ")}
          </div>
        </>
      )}

      {tab === "configuration" && (
        <div data-testid="tr-detail-configuration">
          {cfgLoading ? (
            <div className="text-white/50 text-sm">Loading configuration profile…</div>
          ) : !cfg ? (
            <div className="rounded-2xl border border-white/10 bg-white/5 p-8 text-center text-white/50" data-testid="tr-cfg-empty">
              Configuration profile belum tersedia untuk template ini.
            </div>
          ) : (
            <>
              <div className="rounded-xl border border-cyan-500/30 bg-cyan-500/10 p-3 mb-4">
                <p className="text-cyan-200 text-xs">
                  Configuration Profile 1:1 dengan Template Registry. Provider names only — credential tetap di tenant configuration.
                </p>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="tr-cfg-grid">
                {CONFIG_GROUPS.map((g) => (
                  <Section key={g} title={g.toUpperCase()} icon={Settings2} testid={`tr-cfg-${g}`}>
                    <KeyValueGrid items={cfg[g]} />
                  </Section>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function PlatformTemplateRegistry() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardSource, setWizardSource] = useState("");
  const [publishResult, setPublishResult] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [actionBusy, setActionBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (categoryFilter) params.category = categoryFilter;
      const [listRes, sumRes] = await Promise.all([
        api.get("/platform/template-registry", { params }),
        api.get("/platform/template-registry/summary"),
      ]);
      setItems(listRes.data.items || []);
      setSummary(sumRes.data);
    } catch (e) {
      notifyError(e);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, categoryFilter]);

  useEffect(() => { load(); }, [load]);

  const handlePreview = async (tpl) => {
    setPreviewLoading(true);
    setPreviewData({ name: tpl.name, slug: tpl.slug, status: tpl.status });
    try {
      const r = await api.get(`/platform/template-registry/${tpl.id}/preview`);
      setPreviewData(r.data);
    } catch (e) {
      setPreviewData(null);
      notifyError(e, "Preview gagal dibuat");
    } finally {
      setPreviewLoading(false);
    }
  };

  const handlePublish = async (tpl) => {
    setActionBusy(tpl.id);
    try {
      const r = await api.post(`/platform/template-registry/${tpl.id}/publish`);
      toast.success(`Template "${tpl.name}" berhasil di-publish.`);
      const warns = r.data?.warnings || [];
      if (warns.length) setPublishResult({ name: tpl.name, errors: [], warnings: warns, success: true });
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && Array.isArray(d.errors)) {
        setPublishResult({ name: tpl.name, errors: d.errors, warnings: d.warnings || [], success: false });
      } else {
        notifyError(e, "Gagal publish template");
      }
    } finally {
      setActionBusy(null);
    }
  };

  const handleArchive = async (tpl) => {
    setActionBusy(tpl.id);
    try {
      await api.post(`/platform/template-registry/${tpl.id}/archive`);
      toast.success(`Template "${tpl.name}" di-archive.`);
      load();
    } catch (e) {
      notifyError(e, "Gagal archive template");
    } finally {
      setActionBusy(null);
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter(t =>
      t.name.toLowerCase().includes(q) ||
      t.slug.toLowerCase().includes(q) ||
      t.category?.toLowerCase().includes(q)
    );
  }, [items, query]);

  if (selected) {
    return (
      <PlatformLayout title="Template Registry">
        <DetailView tpl={selected} onBack={() => setSelected(null)} />
      </PlatformLayout>
    );
  }

  return (
    <PlatformLayout title="Template Registry">
      <div className="mb-5">
        <div className="flex items-center gap-3 flex-wrap">
          <div>
            <h1 className="text-white font-display font-black text-2xl">Template Registry</h1>
            <p className="text-white/50 text-xs">Template Ecosystem · Clone & Design tanpa coding</p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Button
              onClick={() => { setWizardSource(""); setWizardOpen(true); }}
              className="bg-gradient-to-r from-cyan-500 to-purple-500 text-white font-black"
              data-testid="tr-open-clone-wizard"
            >
              <Copy size={13} className="mr-1" /> Clone Template
            </Button>
            <Button variant="ghost" onClick={load} className="text-white/70 hover:text-white hover:bg-white/10" data-testid="tr-refresh">
              <RefreshCcw size={12} className="mr-1" /> Refresh
            </Button>
          </div>
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-3 mb-6" data-testid="tr-summary">
          <SummaryCard label="Total" value={summary.total} tone="slate" testid="tr-sum-total" />
          <SummaryCard label="Published" value={summary.published ?? summary.active} tone="emerald" testid="tr-sum-published" />
          <SummaryCard label="Draft" value={summary.draft} tone="amber" testid="tr-sum-draft" />
          <SummaryCard label="Deprecated" value={summary.deprecated} tone="rose" testid="tr-sum-deprecated" />
          <SummaryCard label="Archived" value={summary.archived} tone="slate" testid="tr-sum-archived" />
          <SummaryCard label="Categories" value={summary.categories?.length ?? 0} tone="cyan" testid="tr-sum-cats" />
        </div>
      )}

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Cari template…"
            className="w-full rounded-lg bg-black/30 border border-white/10 text-white px-9 py-2 text-sm"
            data-testid="tr-search"
          />
        </div>
        <div className="flex items-center gap-1 text-white/60">
          <Filter size={14} />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-lg bg-black/30 border border-white/10 text-white px-3 py-2 text-sm"
            data-testid="tr-filter-status"
          >
            <option value="">All Status</option>
            <option value="PUBLISHED">Published</option>
            <option value="DRAFT">Draft</option>
            <option value="ARCHIVED">Archived</option>
          </select>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="rounded-lg bg-black/30 border border-white/10 text-white px-3 py-2 text-sm"
            data-testid="tr-filter-category"
          >
            <option value="">All Categories</option>
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="text-white/50 text-sm" data-testid="tr-loading">Loading templates…</div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-white/5 p-10 text-center text-white/50" data-testid="tr-empty">
          Belum ada template. Template default di-seed via startup — refresh untuk sinkronisasi.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="tr-grid">
          {filtered.map(tpl => (
            <TemplateCard
              key={tpl.id}
              tpl={tpl}
              busy={actionBusy === tpl.id}
              onClick={() => setSelected(tpl)}
              onClone={() => { setWizardSource(tpl.id); setWizardOpen(true); }}
              onDesign={() => navigate(`/platform/template-registry/${tpl.id}/designer`)}
              onPreview={() => handlePreview(tpl)}
              onPublish={() => handlePublish(tpl)}
              onArchive={() => handleArchive(tpl)}
            />
          ))}
        </div>
      )}

      {/* Publish validation result dialog */}
      <Dialog open={!!publishResult} onOpenChange={(v) => { if (!v) setPublishResult(null); }}>
        <DialogContent className="bg-slate-900 border-white/10 text-white max-w-md" data-testid="tr-publish-result-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {publishResult?.success ? (
                <><CheckCircle2 size={18} className="text-emerald-400" /> Published — dengan catatan</>
              ) : (
                <><AlertTriangle size={18} className="text-red-400" /> Cannot Publish</>
              )}
            </DialogTitle>
            <DialogDescription className="text-white/50">
              Template: {publishResult?.name}
            </DialogDescription>
          </DialogHeader>
          {(publishResult?.errors || []).length > 0 && (
            <div className="space-y-1.5" data-testid="tr-publish-errors">
              {publishResult.errors.map((err, i) => (
                <div key={i} className="flex items-start gap-2 rounded-lg border border-red-800/50 bg-red-950/40 px-3 py-2 text-sm text-red-200">
                  <XCircle size={14} className="mt-0.5 shrink-0 text-red-400" /> {err}
                </div>
              ))}
            </div>
          )}
          {(publishResult?.warnings || []).length > 0 && (
            <div className="space-y-1.5" data-testid="tr-publish-warnings">
              {publishResult.warnings.map((w, i) => (
                <div key={i} className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-200">
                  <AlertTriangle size={14} className="mt-0.5 shrink-0 text-amber-400" /> {w}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Isolated template preview dialog */}
      <Dialog open={!!previewData} onOpenChange={(v) => { if (!v) setPreviewData(null); }}>
        <DialogContent className="bg-slate-900 border-white/10 text-white max-w-lg" data-testid="tr-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye size={16} className="text-cyan-400" /> Preview — {previewData?.name}
            </DialogTitle>
            <DialogDescription className="text-white/50">
              Preview terisolasi dari Template Configuration (tidak menyentuh data tenant).
            </DialogDescription>
          </DialogHeader>
          {previewLoading ? (
            <div className="text-white/50 text-sm py-8 text-center">Memuat preview…</div>
          ) : (
            <div className="max-h-[70vh] overflow-y-auto">
              <TemplateLivePreview
                design={previewData?.design}
                dna={previewData?.dna}
                demoContent={previewData?.demo_content}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      <TemplateCloneWizard
        open={wizardOpen}
        onOpenChange={(v) => { setWizardOpen(v); if (!v) load(); }}
        templates={items}
        defaultSourceId={wizardSource}
      />
    </PlatformLayout>
  );
}
